// 'use client';
import { XStack, YStack, Text, Image } from 'tamagui';
import { Link } from 'solito/link';
import { Linking, Platform, useWindowDimensions } from 'react-native';
import { useRouter } from 'solito/navigation';
import {
  useEventZStore,
  useGlobalLoginContext,
  termsPrivacy,
  useAppPersistenceStore,
  KissanEventType,
} from '@ninjacart/nc-nk-store';
import { useTranslation } from 'react-i18next';
import { useQueuedEventStore, addEventParam } from '@ninjacart/nc-events-sdk-v2';
import { useEffect, useState } from 'react';
import { useFeatureFlag } from '@ninjacart/nc-nk-ff4j';

const Footer = ({ sourcepage }: { sourcepage: string }) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { t } = useTranslation();
  const fields = useGlobalLoginContext(['openLoginSheet', 'navigationParams']);
  const setNavigationParams = fields.navigationParams.set;
  const router = useRouter();
  const contactUsWhatsappNumber = useEventZStore((state) => state.contactUsWhatsappNumber);
  const enqueue = useQueuedEventStore.getState()?.enqueue;
  const uniqueDeviceId = useAppPersistenceStore((state) => state.deviceId);
  const requestQnAFeatureFlow = useFeatureFlag(
    'requestQnAFeatureFlow',
    'UserDeviceId',
    uniqueDeviceId!
  );
  const appLanguage = useAppPersistenceStore.getState()?.appLanguage;

  const handleFatFooterEvent = () => {
    const fatFooterEvent = {
      name: KissanEventType.FAT_FOOTER_VIEW,
      screenDetails: { screen: sourcepage },
    };
    enqueue(fatFooterEvent);
  };

  useEffect(() => {
    handleFatFooterEvent();
  }, []);

  const handleWhatsAppContact = async () => {
    const phoneNumber = `${contactUsWhatsappNumber}`;
    const message = 'Hi';
    const whatsappURL = `whatsapp://send?phone=${phoneNumber}&text=${message}`;
    try {
      const supported = await Linking.canOpenURL(whatsappURL);
      if (supported) {
        await Linking.openURL(whatsappURL);
      } else {
        if (Platform.OS === 'ios') {
          Linking.openURL('https://apps.apple.com/us/app/whatsapp-messenger/id310633997');
        } else {
          Linking.openURL('https://play.google.com/store/apps/details?id=com.whatsapp');
        }
      }
    } catch (error) {
      console.error('Failed to open WhatsApp:', error);
    }
  };

  const handleFatFooterClickEvent = (name: string, route: string) => {
    const fatFooterClickEvent = {
      name: KissanEventType.FAT_FOOTER_CLICK,
      screenDetails: { screen: sourcepage },
      eventParams: [addEventParam('item', name), addEventParam('itemUrl', route ?? '')],
    };

    enqueue(fatFooterClickEvent);
  };

  const footerLinks = [
    {
      title: t('page.product'),
      links: [
        {
          route: '/home',
          name: 'videos',
          label: t('page.videos'),
          logInEnabled: false,
          external: false,
        },
        {
          route: `/${appLanguage}/categories`,
          name: 'categories',
          label: t('page.categories'),
          logInEnabled: false,
          external: false,
        },
        { route: '/qna', name: 'faq', label: t('page.faq'), logInEnabled: false, external: false },
        {
          route: '/discover',
          name: 'explore',
          label: t('page.explore'),
          logInEnabled: false,
          external: false,
        },
        {
          route: '/qna/create',
          name: 'askAQuestion',
          label: t('page.askAQuestion'),
          logInEnabled: true,
          external: false,
        },
        {
          route: '/all-qna',
          name: 'allQnaIndex',
          label: t('page.allQnaIndex'),
          logInEnabled: false,
          external: false,
        },
      ],
    },
    {
      title: t('page.company'),
      links: [
        {
          route: '/aboutUs',
          name: 'aboutUs',
          label: t('page.aboutUs'),
          logInEnabled: false,
          external: false,
        },
        {
          route: '/',
          label: t('page.contactUs'),
          name: 'contactUs',
          logInEnabled: false,
          external: true,
        },
      ],
    },
    {
      title: t('page.legal'),
      links: [
        {
          route: termsPrivacy()?.termsConditionsUrl,
          name: 'termsAndConditions',
          label: t('page.termsAndConditions'),
          logInEnabled: false,
          external: true,
        },
        {
          route: 'https://sites.google.com/view/ninja-kisan/privacy-policy',
          name: 'privacyPolicy',
          label: t('page.privacyPolicy'),
          logInEnabled: false,
          external: true,
        },
      ],
    },
  ];

  const navigateFlow = (screenName: string) => {
    fields['openLoginSheet'].set(true);
    fields['previousPage'].set(screenName);
    setNavigationParams({
      name: '/qna/create',
      routeName: 'qna/create/index',
      translate: `${t('page.askQuestionCtaQnaPage')}`,
      description: 'QNA page',
    });
  };

  const FooterLink = ({
    route,
    external,
    logInEnabled,
    name,
    children,
  }: {
    route: string;
    external: boolean;
    logInEnabled?: boolean;
    name: string;
    children: any;
  }) => {
    const handlePress = () => {
      handleFatFooterClickEvent(name, route);
      if (name === 'contactUs') {
        handleWhatsAppContact();
        return;
      }
      if (external) {
        Linking.openURL(route);
        return;
      }
      if (logInEnabled) {
        navigateFlow(name);
        return;
      }
      router.push(route);
    };
    return (
      <Text color="gray" onPress={handlePress} style={{ cursor: 'pointer' }}>
        {children}
      </Text>
    );
  };

  return (
    <YStack
      backgroundColor="$cardbackground"
      paddingHorizontal={16}
      paddingBottom={isMobile ? 80 : 20}
    >
      <XStack
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="flex-start"
        width="100%"
        rowGap="$4"
        columnGap="$4"
      >
        {/* Logo and Social Links */}
        <YStack
          alignItems="flex-start"
          space="$3"
          flexBasis={isMobile ? '100%' : '25%'}
          marginTop={20}
        >
          <Image
            source={{
              uri: 'https://ninjakissan.s3.ap-south-1.amazonaws.com/icon/2025-02-17/Logo3.png',
            }}
            height={43}
            width={130}
          />
          <Text color="$lightGrey" fontSize={14} textAlign={isMobile ? 'center' : 'left'}>
            {t('page.connectWithUs')}
          </Text>
          <XStack space="$3" flexWrap="wrap" alignItems="flex-start">
            <Link href="https://www.instagram.com/ninjakisan.official/" shallow>
              <Image
                source={{
                  uri: 'https://ninjakissan.s3.ap-south-1.amazonaws.com/icon/2025-02-17/Instagram.png',
                }}
                width={20}
                height={20}
              />
            </Link>
            <Link href="https://www.youtube.com/@NinjaKisan" shallow>
              <Image
                source={{
                  uri: 'https://ninjakissan.s3.ap-south-1.amazonaws.com/icon/2025-02-17/YouTube.png',
                }}
                width={20}
                height={20}
              />
            </Link>
          </XStack>
        </YStack>

        {/* Footer Sections */}
        {footerLinks.map(({ title, links }) => (
          <YStack key={title} space="$3" marginTop={20} alignItems="flex-start">
            <Text fontWeight="bold" fontSize={16}>
              {title}
            </Text>
            <YStack space="$2" alignItems="flex-start">
              {links.map(({ route, label, external, logInEnabled, name }) => (
                <FooterLink
                  key={label}
                  route={route}
                  external={external}
                  logInEnabled={logInEnabled}
                  name={name}
                >
                  {label}
                </FooterLink>
              ))}
            </YStack>
          </YStack>
        ))}

        {/* Download App Section */}
        <YStack
          space="$3"
          flexBasis={isMobile ? '45%' : '20%'}
          marginTop={20}
          alignItems="flex-start"
        >
          <Text fontWeight="bold" fontSize={16}>
            {t('page.download')}
          </Text>
          <YStack space="$3" alignItems="flex-start">
            <Link href="http://play.google.com/store/apps/details?id=com.ninja.kisaan" shallow>
              <Image
                source={{
                  uri: 'https://ninjakissan.s3.ap-south-1.amazonaws.com/icon/2025-02-17/Store_download_button.png',
                }}
                width={120}
                height={40}
              />
            </Link>
          </YStack>
        </YStack>
      </XStack>
    </YStack>
  );
};
export { Footer };
